import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { filter, map } from 'rxjs';

import { AuthService } from 'src/app/auth/services/auth.service';
import { LayoutService } from 'src/app/core/layout/layout.service';
import { IconComponent } from 'src/app/features/shared/icons/container/icon.component';
import { ImageBoxComponent } from 'src/app/features/shared/image-boxes/image-box/image-box.component';
import { LogoComponent } from 'src/app/features/shared/logo/logo/logo.component';
import { NavigationHistoryService } from '../../services/navigation-history.service';
import { NavigationStackService } from 'src/app/features/shared/navigation-stack/services/navigation-stack.service';
import { INavHistoryEntry } from '../../models/nav-history-entry';
import { INavTreeNode } from '../../models/nav-tree-node';
import { pageShortLabel } from 'src/app/infrastructure/functions/page-label.function';
import { pageName } from 'src/assets/common/enums/page-name.g';
import { ApplicationUserService } from 'src/app/infrastructure/services/application-user.services/application-user.service';

// Recursieve boom met de laatst bezochte items, meest recente bovenaan. Bovenaan een vaste
// Home-link (klapt niet uit, domeinen blijven dus altijd zichtbaar). Administrators zien de
// admin-boom (domein → admin-childs); ingelogde users de user-boom (domeinen → level2 →
// order-childs). Een knoop met `entry` navigeert bij klik op de regel; een knoop met kinderen
// klapt uit via het pijltje (rechts). Beide secties kunnen tegelijk verschijnen.
@Component({
    selector: 'app-navigation-history',
    templateUrl: './navigation-history.component.html',
    styleUrls: ['./navigation-history.component.scss'],
    standalone: true,
    imports: [NgTemplateOutlet, MatIconModule, IconComponent, ImageBoxComponent, LogoComponent],
})
export class NavigationHistoryComponent {
    private readonly __auth = inject(AuthService);
    private readonly __layout = inject(LayoutService);
    private readonly __router = inject(Router);
    private readonly __navStack = inject(NavigationStackService);
    private readonly __appUserService = inject(ApplicationUserService);
    protected readonly _navHistory = inject(NavigationHistoryService);

    protected readonly _homeLabel = pageShortLabel(pageName.home);
    private readonly __homeUrl = '/home';

    // Uitgeklapte knopen, op id. Standaard alles dicht ⇒ compact in de smalle drawer.
    private readonly __expanded$$ = signal<ReadonlySet<string>>(new Set<string>());

    // Huidige route-URL (reactief): bepaalt welk item als 'actief' oplicht.
    private readonly __url$$ = toSignal(
        this.__router.events.pipe(filter(e => e instanceof NavigationEnd), map(() => this.__router.url)),
        { initialValue: this.__router.url },
    );

    // Id's met de Active-look: het item dat bij de huidige route hoort (de diepste match). Is dat een tab-kop
    // (`...|tab|...`), dan de hele open pagina: de pagina erboven met al zijn tab-koppen (niet hun items eronder).
    private readonly __activeIds$$ = computed<Set<string>>(() => {
        const __path = this.__pathToActive(this.__url$$());
        const __last = __path.at(-1);
        if (!__last) return new Set();
        const __pageId = __last.includes('|tab|') ? __path.at(-2) : undefined;
        const __page = __pageId ? this.__node_find(__pageId) : null;
        if (!__page) return new Set([__last]);
        return new Set([__page.id, __last, ...__page.children.filter(c => !!c.tabPreset).map(c => c.id)]);
    });

    // Home + secties tonen zodra er een ingelogde user is. User-sectie verschijnt zodra er
    // bijbehorende historie is.
    //
    // Admin-sectie: NIET op basis van adminTree$$-inhoud (geprobeerd, teruggedraaid — een gewone
    // gebruiker met stale/eigen "functions"-categorie-historie kon zo alsnog AdminPanel te zien
    // krijgen, zie [[project_navigation_history_drawer_fixes]] Root cause 7) en NIET op basis van
    // een globale claim_check(pageName.adminPanelDetails, null, ...) (ook geprobeerd, ook
    // teruggedraaid — ApplicationUserService.UserClaims wordt door ELK response volledig vervangen
    // en dus alleen vlak ná Home/AdminPanel zelf correct null/null-geschaald). In plaats daarvan:
    // ApplicationUserService.isAdminPanel$$ — een dedicated, niet-pagina-gescoped probe op
    // /api/AdminPanel/details (hetzelfde endpoint + server-side claim-check die de echte
    // AdminPanel-pagina ook gebruikt), eenmalig per ingelogde user, immuun voor zowel
    // claims-overschrijving als stale drawer-historie.
    protected readonly _showHome$$ = computed(() => !!this.__auth.token$$());
    protected readonly _showAdmin$$ = computed(() => this.__appUserService.isAdminPanel$$() === true);
    protected readonly _showUser$$ = computed(() => !!this.__auth.token$$() && this._navHistory.userTree$$().length > 0);

    // Alleen de tabkeuze van het huidige frame — zie het effect in de constructor.
    private readonly __activeTabName$$ = computed(() => this.__navStack.frameCurrent$$()?.tabName ?? null);

    constructor() {
        // Klap bij elke navigatie de boom open tot het item van de huidige route (event-driven,
        // geen signal-write vanuit een effect). Zo toont de drawer bij openen meteen je positie.
        this.__syncExpandToRoute();
        this.__router.events
            .pipe(filter(e => e instanceof NavigationEnd), takeUntilDestroyed())
            .subscribe(() => this.__syncExpandToRoute());

        // Een tab wisselen BINNEN de huidige container (Domain/Project/AdminPanel) is een pure
        // UI-actie van TabListService — geen navigatie, dus geen NavigationEnd — maar verandert wel
        // welke categorie-kop hoort uit te klappen (zie __pathToActive's __exactTab). Zonder dit
        // effect bleef de drawer bij het openen de uitklap-stand van de VORIGE (bv. default-)tab
        // tonen totdat er weer een echte navigatie plaatsvond. frameCurrent$$().tabName is dezelfde
        // signal die TabListService bij elke tab-klik bijwerkt (via PageStorageService, backed door
        // NavigationStackService's huidige frame), dus dit vangt elke tab-wissel alsnog op.
        //
        // Bewust via __activeTabName$$ (string, computed-gelijkheid) en niet rechtstreeks op
        // frameCurrent$$(): dat levert bij ELKE schrijfactie op het huidige frame (scroll-/
        // positie-state, lijst-state, ...) een nieuw object op. Dan resette de drawer zijn uitklap-
        // stand ook terwijl hij open stond — op mobiel (push-drawer ⇒ resize ⇒ positie-writes) klapte
        // een met de hand uitgeklapt Domain zo weer dicht en sprong de boom terug naar de huidige
        // route (bv. AdminPanel), precies op het moment dat je erin wilde tikken.
        effect(() => {
            this.__activeTabName$$();
            untracked(() => this.__syncExpandToRoute());
        });
    }

    isExpanded(node: INavTreeNode): boolean {
        return this.__expanded$$().has(node.id);
    }

    // Het item (op elk niveau) dat hoort bij de pagina die nu open staat ⇒ Active-Background look.
    // Bij openen van de drawer licht zo de huidige locatie op (reactief op de route).
    isActive(node: INavTreeNode): boolean {
        return this.__activeIds$$().has(node.id);
    }

    // Inspring-stappen per knoop: een kop (level n) en zijn items (level n+1) delen dezelfde
    // groeps-index ⇒ de items lijnen links uit met hun kop; een dieper geneste groep schuift op.
    // Items die in elkaar genest zijn zonder kop ertussen (taak in een taak) dragen hun eigen indent.
    groupIndent(node: INavTreeNode): number {
        return node.indent ?? Math.ceil(node.level / 2);
    }

    // Vaste Home-link bovenaan: navigeer naar home en sluit de drawer.
    goHome(): void {
        void this.__router.navigateByUrl(this.__homeUrl);
        this.__layout.toggleSideNav();
    }

    // Navigeer naar het bezochte item (opgeslagen url) en sluit de drawer (toggle: de drawer
    // staat open bij het klikken, dus toggle = sluiten — zelfde patroon als de footer).
    open(entry: INavHistoryEntry): void {
        void this.__router.navigateByUrl(entry.url);
        this.__layout.toggleSideNav();
    }

    // Klik op de regel zelf: navigeer als de knoop een doel heeft, klap anders uit/in. Een
    // categorie-kop met `tabPreset` (zie NavigationHistoryService) selecteert vóór het navigeren
    // ook meteen de bijbehorende tab — zelfde effect als klikken in de echte tabheader.
    rowClick(node: INavTreeNode): void {
        if (node.entry) {
            if (node.tabPreset) this._navHistory.presetTab(node.entry.url, node.tabPreset.tabName);
            this.open(node.entry);
        }
        else if (node.children.length) this.toggle(node);
    }

    // Klik op het pijltje: alleen uit-/inklappen, nooit navigeren. Accordeon-gedrag: bij
    // openklappen sluiten de groepsgenoten op hetzelfde niveau (max 1 open per groep).
    toggle(node: INavTreeNode, event?: Event): void {
        event?.stopPropagation();
        const __next = new Set(this.__expanded$$());
        if (__next.has(node.id)) {
            __next.delete(node.id);
        } else {
            for (const __sib of this.__siblingIds(node)) __next.delete(__sib);
            __next.add(node.id);
            this.__autoExpandSoleGroup(node, __next);
        }
        this.__expanded$$.set(__next);
    }

    // Groepsgenoten van een knoop voor het accordeon-sluiten. Voor wortels (level 0) overspant
    // de groep BEIDE bomen: admin- en user-sectie tonen in de drawer als één visuele lijst, dus
    // een domein openklappen moet ook de admin-wortel sluiten (en omgekeerd). `siblingIds` wordt
    // per boom apart gevuld en bevat die kruislingse wortels niet — daarom hier zelf samenstellen.
    private __siblingIds(node: INavTreeNode): readonly string[] {
        if (node.level === 0) {
            return [...this._navHistory.adminTree$$(), ...this._navHistory.userTree$$()].map(r => r.id);
        }
        return node.siblingIds ?? [];
    }

    // Bij openklappen: zolang de enige keuze één groeps-kop is (precies één kind, zonder eigen
    // doel maar mét kinderen), klap die meteen mee open. Een kop ingeklapt tonen terwijl het de
    // enige optie is, is onlogisch; herhaalt zich zo diep als de keten van enkelvoudige koppen loopt.
    private __autoExpandSoleGroup(node: INavTreeNode, into: Set<string>): void {
        let __cur = node;
        while (__cur.children.length === 1) {
            const __only = __cur.children[0];
            if (__only.entry || !__only.children.length) break;
            into.add(__only.id);
            __cur = __only;
        }
    }

    // Zet de uitklap-state op het pad naar het item van de huidige route (gefocuste accordeon:
    // andere takken sluiten) — inclusief het actieve item zelf: staat bv. een Domain open, dan
    // klapt die meteen open zodat z'n categorie-tabs (Projects, Contacten, ...) meteen zichtbaar
    // zijn. Voor een leaf-item zonder children (bv. een los bezochte Contact) is dit een no-op —
    // die heeft toch niets om uit te klappen. Vindt niets ⇒ alles dicht (bv. op Home).
    private __syncExpandToRoute(): void {
        const __path = this.__pathToActive(this.__router.url);
        this.__expanded$$.set(new Set(__path));
    }

    // Pad (id's wortel → item) naar de knoop die hoort bij de gegeven route-URL. Exacte url-match
    // wint; anders de diepste knoop waarvan de (unieke) guid in de URL voorkomt. Geen match ⇒ [].
    //
    // Elke categorie-kop onder een container (Projects, Contacts, DomainUsers, ...) deelt letterlijk
    // dezelfde `entry` (en dus dezelfde `entry.url`) als de container zelf — ze navigeren allemaal
    // naar de container-url en presetten daar pas een tab (zie NavigationHistoryService.__tabCategoryNode).
    // Een kale url-gelijkheid kan zulke koppen dus niet uit elkaar houden en zou altijd de EERSTE
    // (boomvolgorde) laten "winnen", ongeacht welke tab er werkelijk open staat — vandaar
    // __activeTab erbij: bij een url-match op een categorie-kop telt alleen die kop wiens
    // tabPreset.tabName overeenkomt met de daadwerkelijk geselecteerde tab van het huidige stack-frame
    // (NavigationStackService.frameCurrent$$ — dezelfde bron die TabListService zelf leest/schrijft,
    // dus altijd de echte stand, ongeacht of de tab via de drawer of de eigen tabheader gekozen werd).
    private __node_find(id: string): INavTreeNode | null {
        const __walk = (nodes: INavTreeNode[]): INavTreeNode | null => {
            for (const __n of nodes) {
                if (__n.id === id) return __n;
                const __hit = __walk(__n.children);
                if (__hit) return __hit;
            }
            return null;
        };
        return __walk([...this._navHistory.adminTree$$(), ...this._navHistory.userTree$$()]);
    }

    private __pathToActive(__url: string): string[] {
        const __roots = [...this._navHistory.adminTree$$(), ...this._navHistory.userTree$$()];
        const __activeTab = this.__activeTabName$$();
        let __exact: string[] | null = null;
        let __exactTab: string[] | null = null;
        let __partial: string[] | null = null;
        const __walk = (node: INavTreeNode, trail: string[]): void => {
            const __here = [...trail, node.id];
            if (node.entry?.url === __url) {
                if (!__exact) __exact = __here;
                if (!__exactTab && node.tabPreset && node.tabPreset.tabName === __activeTab) __exactTab = __here;
            }
            const __guid = node.entry?.guid;
            if (__guid && __url.includes(__guid) && (!__partial || __here.length > __partial.length)) __partial = __here;
            for (const __c of node.children) __walk(__c, __here);
        };
        for (const __r of __roots) __walk(__r, []);
        return __exactTab ?? __exact ?? __partial ?? [];
    }
}
